// src/context/cartcontext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { notifyError, notifySuccess, notifyCartAdd, notifyCartRemove } from "../utils/notify";

const CartContext = createContext();
const MAX_PER_PRODUCT = 10;

// ✅ FIX 1: Typo in loadCart function (was `serializedsCart`)
const loadCart = () => {
    try {
        const serializedCart = localStorage.getItem('cartItems');
        return serializedCart ? JSON.parse(serializedCart) : [];
    } catch {
        return [];
    }
};

// Save to localStorage
const saveCart = (items) => {
    try {
        localStorage.setItem('cartItems', JSON.stringify(items));
    } catch {}
};

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState(loadCart);

    useEffect(() => {
        saveCart(cartItems);
    }, [cartItems]);

    const addToCart = (product, quantity = 1) => {
        if (!product || !product.id) return;
        const qtyToAdd = Math.max(1, Math.floor(quantity || 1));

        const fullProduct = {
            id: product.id,
            slug: product.slug || `product-${product.id}`,
            name: product.name || "Unnamed Product",
            price: parseFloat(product.price) || 0,
            image: product.image || null,
            category: product.category || null,
        };

        setCartItems(prevItems => {
            const existing = prevItems.find(i => i.product.id === product.id);
            let updated;

            if (existing) {
                const newQty = Math.min(existing.quantity + qtyToAdd, MAX_PER_PRODUCT);
                updated = prevItems.map(i =>
                    i.product.id === product.id
                        ? { ...i, quantity: newQty }
                        : i
                );
                if (newQty >= MAX_PER_PRODUCT && existing.quantity < MAX_PER_PRODUCT) {
                    notifyError("Quantity limit reached", `Maximum ${MAX_PER_PRODUCT} per product. Quantity capped for ${fullProduct.name}.`);
                } else if (newQty < MAX_PER_PRODUCT) {
                    notifyCartAdd(`${fullProduct.name} (${newQty} in cart)`);
                }
            } else {
                const initialQty = Math.min(qtyToAdd, MAX_PER_PRODUCT);
                updated = [...prevItems, { product: fullProduct, quantity: initialQty }];
                if (initialQty >= MAX_PER_PRODUCT) {
                    notifySuccess("Added to cart", `Maximum ${MAX_PER_PRODUCT} per product. Added ${MAX_PER_PRODUCT} of ${fullProduct.name}.`);
                } else {
                    notifyCartAdd(`${fullProduct.name} (${initialQty} added)`);
                }
            }

            return updated;
        });
    };

    const updateItemQuantity = (productId, qty) => {
        if (qty <= 0) {
            const item = cartItems.find(i => i.product.id === productId);
            if (item) {
                removeFromCart(productId);
            }
            return;
        }
        const safeQty = Math.min(MAX_PER_PRODUCT, Math.max(1, Math.floor(qty)));
        setCartItems(prev => prev.map(i => i.product.id === productId ? { ...i, quantity: safeQty } : i));
        if (qty > MAX_PER_PRODUCT) {
            notifyError("Quantity limit", `You can only buy up to ${MAX_PER_PRODUCT} of one product per order.`);
        }
    };

    const removeFromCart = (productId) => {
        const item = cartItems.find(i => i.product.id === productId);
        const productName = item?.product?.name || "Item";
        setCartItems(prev => prev.filter(i => i.product.id !== productId));
        notifyCartRemove(productName);
    };

    const emptyCart = () => {
        setCartItems([]);
        notifySuccess("Cart emptied", "All items have been removed from your cart.");
    };

    const cartTotal = cartItems.reduce(
        (sum, i) => sum + (parseFloat(i.product.price) || 0) * (i.quantity || 0),
        0
    );

    return (
        <CartContext.Provider value={{
            cartItems,
            cartTotal,
            cartCount: cartItems.length,
            addToCart,
            updateItemQuantity,
            removeFromCart,
            emptyCart
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) throw new Error("useCart must be inside CartProvider");
    return context;
};
